/**
 * Staging Area Segmentation
 *
 * Handles finding a 3-segment spot chain for staging workflow:
 * 1. Arrival staging (fixed duration in staging zone)
 * 2. Permanent parking (remaining time in non-staging zone)
 * 3. Departure staging (fixed duration in staging zone)
 */

import { dwellMask, intersects } from "./bitmap";
import { countTrappedAircraft, findTrappedAircraft } from "./egress";
import { Point } from "./geometryUtils";
import {
  hasGeometryCollision,
  isDurationValid,
  isSpotEnabled,
} from "./spotFiltering";
import { isSpotInStagingZone } from "./stagingArea";
import {
  LogEntry,
  MINUTES_PER_BUCKET,
  ParkingRequest,
  Placement,
  PlacementSegment,
  Spot,
  Zone,
} from "./types";
import { getAircraftDisplayName } from "./utils/aircraftUtils";
import { createTaggedLogger } from "./utils/logger";
import {
  calculateSpotWaste,
  calculateStructuralComplexity,
} from "./wasteScoring";

const logger = createTaggedLogger("STAGING");

/**
 * Result from finding staging chain
 */
export type StagingChainResult =
  | { success: true; segments: PlacementSegment[]; moveCount: number }
  | {
      success: false;
      reason: string;
      failedSegment: "arrival" | "permanent" | "departure";
    };

/**
 * Find 3-segment spot chain for staging workflow
 *
 * Creates fixed time windows for staging workflow:
 * - Segment 1: Arrival staging (0 to stagingDwellBuckets)
 * - Segment 2: Permanent (stagingDwellBuckets to end-stagingDwellBuckets)
 * - Segment 3: Departure staging (end-stagingDwellBuckets to end)
 *
 * Each segment is independently placed using first-fit within constraints:
 * - Size compatibility (aircraft must fit in spot)
 * - Enabled status (spot and zone not disabled)
 * - Zone type (staging segments → staging zones, permanent → permanent zones)
 * - Time availability (no bitmap conflicts)
 *
 * Note: Buffer is NOT applied to staging segments - the staging dwell time
 * already includes operational buffer for servicing operations.
 *
 * @param request - Original parking request
 * @param spots - All available spots
 * @param stagingDwellBuckets - Time to spend in staging (in 15-min buckets)
 * @param zones - Zones for disabled status checking
 * @param horizonHours - Planning horizon
 * @returns Staging chain result with segments or failure reason
 *
 * @example
 * ```typescript
 * const result = findStagingChain(
 *   { id: "AC1", arrivalBucket: 0, departureBucket: 40, sizeCategory: "small" }, // 10 hours
 *   spots,
 *   3, // 45 min staging
 *   zones, // For disabled checking
 *   24 // horizonHours
 * );
 *
 * if (result.success) {
 *   // result.segments = [
 *   //   { spotId: "S1", orientationId: "center", startBucket: 0, endBucket: 3 },
 *   //   { spotId: "A5", orientationId: "center", startBucket: 3, endBucket: 37 },
 *   //   { spotId: "S2", orientationId: "center", startBucket: 37, endBucket: 40 }
 *   // ]
 *   // result.moveCount = 2
 * }
 * ```
 */
export function findStagingChain(
  request: ParkingRequest,
  spots: Spot[],
  useArrival: boolean,
  useDeparture: boolean,
  arrivalStagingDwellBuckets: number,
  departureStagingDwellBuckets: number,
  zones: Zone[],
  horizonHours: number,
  placement: Placement,
  allRequests: ParkingRequest[],
  egressWeight: number,
  wasteWeight: number,
  structuralWeight: number,
  zonePriorityWeight: number,
  minDwellWeight: number,
  stickinessWeight: number,
  placedAircraftPolygons: Map<string, Point[]>,
  logEntry: (entry: LogEntry) => void
): StagingChainResult {
  const { arrivalBucket, departureBucket } = request;

  // Calculate segment time windows based on enabled staging
  const arrivalStagingEnd = useArrival
    ? arrivalBucket + arrivalStagingDwellBuckets
    : arrivalBucket;
  const departureStagingStart = useDeparture
    ? departureBucket - departureStagingDwellBuckets
    : departureBucket;

  // Filter to enabled spots only (not disabled, zone not disabled)
  const enabledSpots = spots.filter((s) => isSpotEnabled(s, zones));

  // Separate enabled spots into staging and permanent
  const stagingSpots = enabledSpots.filter((s) => isSpotInStagingZone(s));
  const permanentSpots = enabledSpots.filter((s) => !isSpotInStagingZone(s));

  logger.debug(
    `[findStagingChain] ${request.tail_number}: ` +
      `${stagingSpots.length} staging spots, ${permanentSpots.length} permanent spots, ` +
      `useArrival=${useArrival}, useDeparture=${useDeparture}`
  );

  // Arrival staging spot (conditional)
  let arrivalSpot: Spot | null = null;

  if (useArrival) {
    // Find BEST staging spot for arrival (with scoring)
    // Note: Buffer NOT applied - staging dwell time already includes operational buffer
    const arrivalSegmentRequest: ParkingRequest = {
      ...request,
      arrivalBucket,
      departureBucket: arrivalStagingEnd,
    };
    const arrivalMask = dwellMask(arrivalSegmentRequest, 0, horizonHours);

    // Filter to available staging spots (with all hard constraints)
    const availableArrivalSpots = stagingSpots.filter(
      (spot) =>
        spot.allowedSizes.includes(request.sizeCategory) &&
        !intersects(spot.occupancy, arrivalMask) &&
        isDurationValid(spot, arrivalSegmentRequest) &&
        isSpotEnabled(spot, zones) &&
        !hasGeometryCollision(
          spot,
          arrivalSegmentRequest,
          placement,
          placedAircraftPolygons
        )
    );

    if (availableArrivalSpots.length === 0) {
      return {
        success: false,
        reason: `No staging spot available for arrival (${arrivalBucket}-${arrivalStagingEnd}, size: ${request.sizeCategory})`,
        failedSegment: "arrival",
      };
    }

    // Score and pick best staging spot for arrival
    // Staging segments: NO waste penalty (exempted), but apply zone/egress/structural/min_dwell/stickiness
    const scoredArrivalSpots = availableArrivalSpots.map((spot) => {
      const egressScore =
        countTrappedAircraft(
          arrivalSegmentRequest,
          spot,
          placement,
          allRequests,
          spots
        ) * egressWeight;
      const structuralPenalty =
        calculateStructuralComplexity(spot) * structuralWeight;

      const zone = zones.find((z) => z.id === spot.zoneId);
      const zonePriority = zone?.priority ?? 5;
      const zonePenalty = (10 - zonePriority) * zonePriorityWeight;

      // Min dwell penalty: prefer spots that match dwell time requirements
      const dwellMinutes =
        (arrivalSegmentRequest.departureBucket -
          arrivalSegmentRequest.arrivalBucket) *
        MINUTES_PER_BUCKET;
      let minDwellPenalty = 0;
      if (spot.min_dwell_minutes) {
        minDwellPenalty =
          dwellMinutes >= spot.min_dwell_minutes
            ? -5 * minDwellWeight // Bonus for long stays matching remote spot requirements
            : 5 * minDwellWeight; // Penalty for short stays in remote spots
      }

      // Stickiness penalty: preserve existing assignments (last-minute insertion)
      const prevSpotExists =
        request.prevSpotId && spots.find((s) => s.id === request.prevSpotId);
      const stickyPenalty =
        prevSpotExists && request.prevSpotId !== spot.id ? stickinessWeight : 0;

      // Waste penalty EXEMPTED for staging segments
      const totalScore =
        egressScore +
        structuralPenalty +
        zonePenalty +
        minDwellPenalty +
        stickyPenalty;

      return { spot, score: totalScore };
    });

    scoredArrivalSpots.sort((a, b) => a.score - b.score);

    // Log section header
    logEntry({
      aircraftId: request.id,
      type: "section-header",
      reason: `=== Arrival Staging Selection (${availableArrivalSpots.length} candidates) ===`,
    });

    // Log top 10 arrival staging candidates
    const topArrivalCandidates = scoredArrivalSpots.slice(0, 10);
    for (const { spot } of topArrivalCandidates) {
      const trappedAircraft = findTrappedAircraft(
        arrivalSegmentRequest,
        spot,
        placement,
        allRequests,
        spots
      );
      const trappedCount = trappedAircraft.length;

      const egressScore = trappedCount * egressWeight;
      const structuralComplexity = calculateStructuralComplexity(spot);
      const structuralPenalty = structuralComplexity * structuralWeight;

      const zone = zones.find((z) => z.id === spot.zoneId);
      const zonePriority = zone?.priority ?? 5;
      const zonePenalty = (10 - zonePriority) * zonePriorityWeight;

      // Waste exemption for staging segments
      const wasteScore = calculateSpotWaste(arrivalSegmentRequest, spot);
      const wastePenalty = 0; // Exempted for staging

      logEntry({
        aircraftId: request.id,
        spotId: spot.id,
        type: "eval",
        timeAvailable: true,
        trappedCount,
        egressScore,
        egressWeight,
        structuralComplexity,
        structuralWeight,
        structuralPenalty,
        wasteScore,
        wasteWeight,
        wastePenalty,
        zonePriority,
        zonePriorityWeight,
        zonePenalty,
        blockedAircraft:
          trappedAircraft.length > 0
            ? trappedAircraft.map((ac) => getAircraftDisplayName(ac))
            : undefined,
        reason: `[Arrival Staging] Evaluation`,
      });
    }

    arrivalSpot = scoredArrivalSpots[0].spot;

    logger.info(
      `${request.tail_number} arrival: Selected ${arrivalSpot.id} ` +
        `(score: ${scoredArrivalSpots[0].score.toFixed(2)} from ${
          availableArrivalSpots.length
        } candidates)`
    );
  }

  // Segment 2: Find BEST permanent spot for middle period (with full scoring)
  const permanentSegmentRequest: ParkingRequest = {
    ...request,
    arrivalBucket: arrivalStagingEnd,
    departureBucket: departureStagingStart,
  };
  const permanentMask = dwellMask(permanentSegmentRequest, 0, horizonHours);

  // Filter to available permanent spots (with all hard constraints)
  const availablePermanentSpots = permanentSpots.filter(
    (spot) =>
      spot.allowedSizes.includes(request.sizeCategory) &&
      !intersects(spot.occupancy, permanentMask) &&
      isDurationValid(spot, permanentSegmentRequest) &&
      isSpotEnabled(spot, zones) &&
      !hasGeometryCollision(
        spot,
        permanentSegmentRequest,
        placement,
        placedAircraftPolygons
      )
  );

  if (availablePermanentSpots.length === 0) {
    return {
      success: false,
      reason: `No permanent spot available for main dwell (${arrivalStagingEnd}-${departureStagingStart}, size: ${request.sizeCategory})`,
      failedSegment: "permanent",
    };
  }

  // Score and pick best permanent spot
  // Permanent segment: Apply ALL penalties (waste, zone, egress, structural, min_dwell, stickiness)
  const scoredPermanentSpots = availablePermanentSpots.map((spot) => {
    const egressScore =
      countTrappedAircraft(
        permanentSegmentRequest,
        spot,
        placement,
        allRequests,
        spots
      ) * egressWeight;
    const structuralPenalty =
      calculateStructuralComplexity(spot) * structuralWeight;
    const wasteScore = calculateSpotWaste(permanentSegmentRequest, spot);
    const wastePenalty = wasteScore * wasteWeight; // Normal waste penalty applies

    const zone = zones.find((z) => z.id === spot.zoneId);
    const zonePriority = zone?.priority ?? 5;
    const zonePenalty = (10 - zonePriority) * zonePriorityWeight;

    // Min dwell penalty: prefer spots that match dwell time requirements
    const dwellMinutes =
      (permanentSegmentRequest.departureBucket -
        permanentSegmentRequest.arrivalBucket) *
      MINUTES_PER_BUCKET;
    let minDwellPenalty = 0;
    if (spot.min_dwell_minutes) {
      minDwellPenalty =
        dwellMinutes >= spot.min_dwell_minutes
          ? -5 * minDwellWeight // Bonus for long stays matching remote spot requirements
          : 5 * minDwellWeight; // Penalty for short stays in remote spots
    }

    // Stickiness penalty: preserve existing assignments (last-minute insertion)
    const prevSpotExists =
      request.prevSpotId && spots.find((s) => s.id === request.prevSpotId);
    const stickyPenalty =
      prevSpotExists && request.prevSpotId !== spot.id ? stickinessWeight : 0;

    const totalScore =
      egressScore +
      structuralPenalty +
      wastePenalty +
      zonePenalty +
      minDwellPenalty +
      stickyPenalty;

    return { spot, score: totalScore };
  });

  scoredPermanentSpots.sort((a, b) => a.score - b.score);

  // Log section header
  logEntry({
    aircraftId: request.id,
    type: "section-header",
    reason: `=== Permanent Spot Selection (${availablePermanentSpots.length} candidates) ===`,
  });

  // Log top 10 permanent spot candidates
  const topPermanentCandidates = scoredPermanentSpots.slice(0, 10);
  for (const { spot } of topPermanentCandidates) {
    const trappedAircraft = findTrappedAircraft(
      permanentSegmentRequest,
      spot,
      placement,
      allRequests,
      spots
    );
    const trappedCount = trappedAircraft.length;

    const egressScore = trappedCount * egressWeight;
    const structuralComplexity = calculateStructuralComplexity(spot);
    const structuralPenalty = structuralComplexity * structuralWeight;

    const zone = zones.find((z) => z.id === spot.zoneId);
    const zonePriority = zone?.priority ?? 5;
    const zonePenalty = (10 - zonePriority) * zonePriorityWeight;

    // Normal waste penalty for permanent segments
    const wasteScore = calculateSpotWaste(permanentSegmentRequest, spot);
    const wastePenalty = wasteScore * wasteWeight;

    logEntry({
      aircraftId: request.id,
      spotId: spot.id,
      type: "eval",
      timeAvailable: true,
      trappedCount,
      egressScore,
      egressWeight,
      structuralComplexity,
      structuralWeight,
      structuralPenalty,
      wasteScore,
      wasteWeight,
      wastePenalty,
      zonePriority,
      zonePriorityWeight,
      zonePenalty,
      blockedAircraft:
        trappedAircraft.length > 0
          ? trappedAircraft.map((ac) => getAircraftDisplayName(ac))
          : undefined,
      reason: `[Permanent] Evaluation`,
    });
  }

  const permanentSpot = scoredPermanentSpots[0].spot;

  logger.info(
    `${request.tail_number} permanent: Selected ${permanentSpot.id} ` +
      `(score: ${scoredPermanentSpots[0].score.toFixed(2)} from ${
        availablePermanentSpots.length
      } candidates)`
  );

  // Departure staging spot (conditional)
  let departureSpot: Spot | null = null;

  if (useDeparture) {
    // Find BEST staging spot for departure (with scoring)
    const departureSegmentRequest: ParkingRequest = {
      ...request,
      arrivalBucket: departureStagingStart,
      departureBucket,
    };
    const departureMask = dwellMask(departureSegmentRequest, 0, horizonHours);

    // Filter to available staging spots (with all hard constraints)
    const availableDepartureSpots = stagingSpots.filter(
      (spot) =>
        spot.allowedSizes.includes(request.sizeCategory) &&
        !intersects(spot.occupancy, departureMask) &&
        isDurationValid(spot, departureSegmentRequest) &&
        isSpotEnabled(spot, zones) &&
        !hasGeometryCollision(
          spot,
          departureSegmentRequest,
          placement,
          placedAircraftPolygons
        )
    );

    if (availableDepartureSpots.length === 0) {
      return {
        success: false,
        reason: `No staging spot available for departure (${departureStagingStart}-${departureBucket}, size: ${request.sizeCategory})`,
        failedSegment: "departure",
      };
    }

    // Score and pick best staging spot for departure
    // Staging segments: NO waste penalty (exempted), but apply zone/egress/structural/min_dwell/stickiness
    const scoredDepartureSpots = availableDepartureSpots.map((spot) => {
      const egressScore =
        countTrappedAircraft(
          departureSegmentRequest,
          spot,
          placement,
          allRequests,
          spots
        ) * egressWeight;
      const structuralPenalty =
        calculateStructuralComplexity(spot) * structuralWeight;

      const zone = zones.find((z) => z.id === spot.zoneId);
      const zonePriority = zone?.priority ?? 5;
      const zonePenalty = (10 - zonePriority) * zonePriorityWeight;

      // Min dwell penalty: prefer spots that match dwell time requirements
      const dwellMinutes =
        (departureSegmentRequest.departureBucket -
          departureSegmentRequest.arrivalBucket) *
        MINUTES_PER_BUCKET;
      let minDwellPenalty = 0;
      if (spot.min_dwell_minutes) {
        minDwellPenalty =
          dwellMinutes >= spot.min_dwell_minutes
            ? -5 * minDwellWeight // Bonus for long stays matching remote spot requirements
            : 5 * minDwellWeight; // Penalty for short stays in remote spots
      }

      // Stickiness penalty: preserve existing assignments (last-minute insertion)
      const prevSpotExists =
        request.prevSpotId && spots.find((s) => s.id === request.prevSpotId);
      const stickyPenalty =
        prevSpotExists && request.prevSpotId !== spot.id ? stickinessWeight : 0;

      // Waste penalty EXEMPTED for staging segments
      const totalScore =
        egressScore +
        structuralPenalty +
        zonePenalty +
        minDwellPenalty +
        stickyPenalty;

      return { spot, score: totalScore };
    });

    scoredDepartureSpots.sort((a, b) => a.score - b.score);

    // Log section header
    logEntry({
      aircraftId: request.id,
      type: "section-header",
      reason: `=== Departure Staging Selection (${availableDepartureSpots.length} candidates) ===`,
    });

    // Log top 10 departure staging candidates
    const topDepartureCandidates = scoredDepartureSpots.slice(0, 10);
    for (const { spot } of topDepartureCandidates) {
      const trappedAircraft = findTrappedAircraft(
        departureSegmentRequest,
        spot,
        placement,
        allRequests,
        spots
      );
      const trappedCount = trappedAircraft.length;

      const egressScore = trappedCount * egressWeight;
      const structuralComplexity = calculateStructuralComplexity(spot);
      const structuralPenalty = structuralComplexity * structuralWeight;

      const zone = zones.find((z) => z.id === spot.zoneId);
      const zonePriority = zone?.priority ?? 5;
      const zonePenalty = (10 - zonePriority) * zonePriorityWeight;

      // Waste exemption for staging segments
      const wasteScore = calculateSpotWaste(departureSegmentRequest, spot);
      const wastePenalty = 0; // Exempted for staging

      logEntry({
        aircraftId: request.id,
        spotId: spot.id,
        type: "eval",
        timeAvailable: true,
        trappedCount,
        egressScore,
        egressWeight,
        structuralComplexity,
        structuralWeight,
        structuralPenalty,
        wasteScore,
        wasteWeight,
        wastePenalty,
        zonePriority,
        zonePriorityWeight,
        zonePenalty,
        blockedAircraft:
          trappedAircraft.length > 0
            ? trappedAircraft.map((ac) => getAircraftDisplayName(ac))
            : undefined,
        reason: `[Departure Staging] Evaluation`,
      });
    }

    departureSpot = scoredDepartureSpots[0].spot;

    logger.info(
      `${request.tail_number} departure: Selected ${departureSpot.id} ` +
        `(score: ${scoredDepartureSpots[0].score.toFixed(2)} from ${
          availableDepartureSpots.length
        } candidates)`
    );
  }

  // Build segment chain dynamically based on enabled staging
  const segments: PlacementSegment[] = [];

  // Add arrival staging segment if enabled
  if (useArrival && arrivalSpot) {
    segments.push({
      spotId: arrivalSpot.id,
      orientationId: arrivalSpot.orientations[0].id,
      startBucket: arrivalBucket,
      endBucket: arrivalStagingEnd,
    });
  }

  // Always add permanent segment (time range adjusted for enabled staging)
  segments.push({
    spotId: permanentSpot.id,
    orientationId: permanentSpot.orientations[0].id,
    startBucket: arrivalStagingEnd, // Adjusted if arrival staging enabled
    endBucket: departureStagingStart, // Adjusted if departure staging enabled
  });

  // Add departure staging segment if enabled
  if (useDeparture && departureSpot) {
    segments.push({
      spotId: departureSpot.id,
      orientationId: departureSpot.orientations[0].id,
      startBucket: departureStagingStart,
      endBucket: departureBucket,
    });
  }

  // Build readable chain description
  const chainDescription = segments
    .map((seg, idx) => {
      const duration = (seg.endBucket - seg.startBucket) * MINUTES_PER_BUCKET;
      const type =
        idx === 0 && useArrival
          ? "arrival"
          : idx === segments.length - 1 && useDeparture
          ? "departure"
          : "permanent";
      return `${seg.spotId} (${type}, ${duration}m)`;
    })
    .join(" → ");

  logger.info(
    `${request.tail_number}: ✓ Staging chain found (${segments.length} segments): ${chainDescription}`
  );

  return {
    success: true,
    segments,
    moveCount: segments.length - 1, // Dynamic: 0, 1, or 2 moves
  };
}
